import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useT } from '../../i18n';

/** Guía del Centro de ayuda tal como la entrega el API (`GET help/:topic`). */
export interface HelpGuideData {
  topic: string;
  title: string;
  audience: string;
  platforms: string;
  duration: string;
  updated: string;
  lang: string;
  html: string;
  video: { url: string; expiresAt: string } | null;
}

interface GuideContentProps {
  guide: HelpGuideData;
  /** Pide de nuevo la guía (el enlace firmado del video vence en minutos). */
  onVideoExpired?: () => void;
  /** En el panel lateral el texto es un poco más compacto. */
  compact?: boolean;
}

/** Video y pasos de una guía. Se usa en la página de la guía y en el panel «Ayuda de esta pantalla». */
const GuideContent: React.FC<GuideContentProps> = ({ guide, onVideoExpired, compact = false }) => {
  const t = useT();
  const videoExpired = () => !!guide.video && Date.parse(guide.video.expiresAt) - Date.now() < 30_000;
  return (
    <>
      <Box sx={{ my: compact ? 2 : 3 }}>
        {guide.video ? (
          <Box
            component="video"
            controls
            preload="metadata"
            src={guide.video.url}
            // Disuade la descarga; el enlace además vence en minutos.
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
            onPlay={() => {
              if (videoExpired()) onVideoExpired?.();
            }}
            sx={{ display: 'block', width: '100%', maxHeight: '70vh', borderRadius: compact ? 2 : 3, bgcolor: 'common.black', aspectRatio: '16 / 9' }}
          />
        ) : (
          <Alert severity="info">{t('help.noVideo')}</Alert>
        )}
      </Box>
      <Box
        className="help-guide"
        // Contenido de las guías del repositorio de Ruttia (Markdown convertido en el API, sin HTML crudo).
        dangerouslySetInnerHTML={{ __html: guide.html }}
        sx={{
          minWidth: 0,
          overflowWrap: 'anywhere',
          lineHeight: 1.65,
          fontSize: compact ? '0.95rem' : undefined,
          '& h2': { fontSize: compact ? '1.15rem' : '1.35rem', fontWeight: 700, mt: compact ? 3 : 4, mb: 1 },
          '& h3': { fontSize: '1.05rem', fontWeight: 700, mt: 3, mb: 1 },
          '& p, & li': { color: 'text.primary' },
          '& ol, & ul': { pl: 3 },
          '& li + li': { mt: 0.75 },
          '& a': { color: 'primary.main' },
          '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 1, fontSize: '0.9em' },
          // Las tablas se desplazan dentro de su propia área; la página nunca se mueve a los lados.
          '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto', borderCollapse: 'collapse', my: 2, fontSize: '0.92rem' },
          '& th, & td': { border: 1, borderColor: 'divider', px: 1.25, py: 0.75, textAlign: 'left', verticalAlign: 'top', minWidth: 120 },
          '& th': { bgcolor: 'action.hover', fontWeight: 700, position: 'sticky', top: 0 },
        }}
      />
    </>
  );
};

export default GuideContent;
